// src/api/dashboardVersions.ts
//
// A dashboard's version history (plan 2026-10-05-dashboard-version-history,
// §A3 + §A6): list, read, name, checkpoint, restore and open as copy. The
// types are the generated ones (`make generate-types`), so a backend DTO
// change shows up here as a type error instead of a silent drift.
//
// WHY A MODULE OF ITS OWN. `dashboards.ts` is the page harnesses' wholesale
// mock target and already holds the older published-only pair
// (`listDashboardVersions` / `restoreDashboardVersion`). The request wrappers
// are the shared ones from `dashboardMutations.ts` (ADR-0228), so the page
// refreshes the row's revision and review state after a restore or a save
// exactly as it does after any other write.
import { apiJson, apiNoContent } from "./dashboardMutations";
import type { components } from "@verity/shared-types";
import type { DashboardCopyResponse } from "./dashboards";

export type DashboardVersion = components["schemas"]["DashboardVersionDTO"];
export type DashboardVersionKind = DashboardVersion["kind"];
export type DashboardVersionChanges = components["schemas"]["DashboardVersionChangesDTO"];
export type DashboardVersionDetail = components["schemas"]["DashboardVersionDetailDTO"];
export type DashboardVersionRestoreResult = components["schemas"]["DashboardVersionRestoreResponse"];

/** `published`: what was published, for anyone who can read the dashboard.
 *  `all`: every kind; needs `permissions.view_history` (403 otherwise). */
export type DashboardVersionScope = "published" | "all";

function base(dashboardId: string): string {
  return `/dashboards/${encodeURIComponent(dashboardId)}/versions`;
}

/** Newest first. Paginate by passing the smallest `version_number` of the
 *  previous page as `before`. */
export async function listVersions(
  dashboardId: string,
  params: { scope: DashboardVersionScope; before?: number; limit?: number }
): Promise<DashboardVersion[]> {
  const query = new URLSearchParams({ scope: params.scope });
  if (params.before != null) query.set("before", String(params.before));
  if (params.limit != null) query.set("limit", String(params.limit));
  return apiJson<DashboardVersion[]>(`${base(dashboardId)}?${query.toString()}`);
}

/** One version, plus what restoring it would change. */
export async function getVersion(
  dashboardId: string,
  versionNumber: number
): Promise<DashboardVersionDetail> {
  return apiJson<DashboardVersionDetail>(`${base(dashboardId)}/${versionNumber}`);
}

/** Save the current state under a name (non-empty). When nothing changed
 *  since the latest version, that version gets the name instead. */
export async function saveNamedVersion(dashboardId: string, name: string): Promise<DashboardVersion> {
  return apiJson<DashboardVersion>(base(dashboardId), {
    method: "POST",
    body: JSON.stringify({ name }),
  });
}

/** Store the current state as an automatic version (204). */
export async function checkpointVersion(dashboardId: string): Promise<void> {
  await apiNoContent(`${base(dashboardId)}/checkpoint`, { method: "POST" });
}

/** Rename a version, or clear its name with `null`. */
export async function renameVersion(
  dashboardId: string,
  versionNumber: number,
  name: string | null
): Promise<DashboardVersion> {
  return apiJson<DashboardVersion>(`${base(dashboardId)}/${versionNumber}`, {
    method: "PATCH",
    body: JSON.stringify({ name }),
  });
}

/** Restore into the dashboard's draft. `revision` is the draft's revision
 *  the caller saw; the server answers 409 when it moved since. */
export async function restoreVersion(
  dashboardId: string,
  versionNumber: number,
  revision: number
): Promise<DashboardVersionRestoreResult> {
  return apiJson<DashboardVersionRestoreResult>(`${base(dashboardId)}/${versionNumber}/restore`, {
    method: "POST",
    body: JSON.stringify({}),
    headers: { "If-Match": String(revision) },
  });
}

/** Open a version as a new personal dashboard. */
export async function copyVersion(
  dashboardId: string,
  versionNumber: number,
  payload: { name?: string } = {}
): Promise<DashboardCopyResponse> {
  return apiJson<DashboardCopyResponse>(`${base(dashboardId)}/${versionNumber}/copy`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}
